/**
 * Validacion de las URL de destino.
 *
 * Es la defensa mas importante del producto. Un generador de QR es un vector
 * clasico de phishing ("quishing"): el usuario no ve a donde va hasta que ya
 * esta alli. Si alguien usa el servicio para distribuir malware, el dominio
 * corto entero acaba en las listas negras de Chrome y Safari y deja de
 * funcionar para todos los clientes. Por eso se valida al crear y al editar,
 * en el servidor, y nunca solo en el navegador.
 *
 * Aparte del phishing, se bloquea el redirect hacia redes privadas: un QR que
 * apunte a 192.168.1.1 o al endpoint de metadatos de la nube convierte el
 * navegador de la victima en un proxy hacia su propia red interna.
 */

export const MAX_TARGET_URL_LENGTH = 2048;

export type UrlRejectionReason =
  | 'empty'
  | 'too_long'
  | 'malformed'
  | 'scheme_not_allowed'
  | 'embedded_credentials'
  | 'private_host'
  | 'loopback_host'
  | 'link_shortener_loop'
  | 'blocked_host';

export interface UrlCheckResult {
  readonly ok: boolean;
  readonly reason?: UrlRejectionReason;
  /** URL normalizada, lista para guardar. Solo si `ok`. */
  readonly normalized?: string;
}

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

/** Sufijos de host que nunca salen a la internet publica. */
const PRIVATE_HOST_SUFFIXES = ['.local', '.internal', '.localhost', '.home.arpa'];

const LOOPBACK_HOSTNAMES = new Set(['localhost', '0.0.0.0', '[::]', '[::1]', '::1']);

/** Host de metadatos de las nubes: leerlo filtra credenciales de la instancia. */
const METADATA_HOSTNAMES = new Set([
  '169.254.169.254',
  'metadata.google.internal',
  'metadata.goog',
]);

function isIpv4(hostname: string): boolean {
  return /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname);
}

function ipv4Octets(hostname: string): readonly number[] {
  return hostname.split('.').map((part) => Number.parseInt(part, 10));
}

/** Rangos reservados de RFC 1918, loopback, link-local, CGNAT y similares. */
function isPrivateIpv4(hostname: string): boolean {
  const [a, b] = ipv4Octets(hostname);
  if (a === undefined || b === undefined) return true;
  if (a === 0 || a === 10 || a === 127) return true;
  if (a === 169 && b === 254) return true; // link-local, incluye metadatos
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT
  if (a >= 224) return true; // multicast y reservado
  return false;
}

function isPrivateIpv6(hostname: string): boolean {
  const bare = hostname.replace(/^\[|\]$/g, '').toLowerCase();
  if (bare === '::1' || bare === '::') return true;
  if (bare.startsWith('fe80')) return true; // link-local
  if (/^f[cd]/.test(bare)) return true; // unique local fc00::/7
  // IPv4 empotrada en IPv6. Ojo: `URL` normaliza ::ffff:192.168.0.1 a su
  // forma hexadecimal ::ffff:c0a8:1, asi que hay que cubrir las dos escrituras
  // o el bloqueo se esquiva con solo cambiar la notacion.
  const mapped = /^::ffff:(.+)$/.exec(bare);
  if (mapped?.[1]) {
    const rest = mapped[1];
    if (rest.includes('.')) return isPrivateIpv4(rest);

    const groups = rest.split(':');
    if (groups.length === 2) {
      const high = Number.parseInt(groups[0] ?? '', 16);
      const low = Number.parseInt(groups[1] ?? '', 16);
      if (Number.isFinite(high) && Number.isFinite(low)) {
        const octets = [high >> 8, high & 0xff, low >> 8, low & 0xff];
        return isPrivateIpv4(octets.join('.'));
      }
    }
    // Forma mapeada que no sabemos interpretar: se bloquea por precaucion.
    return true;
  }
  return false;
}

/**
 * Comprueba una URL de destino introducida por el usuario.
 *
 * @param input       URL tal cual la escribio el usuario.
 * @param ownHostnames Hosts propios del servicio (dominio corto y sus alias).
 *                    Apuntar un QR a nuestro propio acortador crea un bucle
 *                    de redirecciones que consume invocaciones sin fin.
 */
export function checkTargetUrl(
  input: string,
  ownHostnames: readonly string[] = [],
): UrlCheckResult {
  const raw = input.trim();
  if (raw.length === 0) return { ok: false, reason: 'empty' };
  if (raw.length > MAX_TARGET_URL_LENGTH) return { ok: false, reason: 'too_long' };

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, reason: 'malformed' };
  }

  if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
    // Aqui caen javascript:, data:, file:, intent: y demas.
    return { ok: false, reason: 'scheme_not_allowed' };
  }

  // https://usuario:clave@banco.com/ se lee como "banco.com" de un vistazo.
  if (url.username !== '' || url.password !== '') {
    return { ok: false, reason: 'embedded_credentials' };
  }

  const hostname = url.hostname.toLowerCase();
  if (hostname === '') return { ok: false, reason: 'malformed' };

  if (LOOPBACK_HOSTNAMES.has(hostname)) return { ok: false, reason: 'loopback_host' };
  if (METADATA_HOSTNAMES.has(hostname)) return { ok: false, reason: 'private_host' };
  if (PRIVATE_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))) {
    return { ok: false, reason: 'private_host' };
  }
  if (isIpv4(hostname) && isPrivateIpv4(hostname)) {
    return { ok: false, reason: 'private_host' };
  }
  if (hostname.includes(':') && isPrivateIpv6(hostname)) {
    return { ok: false, reason: 'private_host' };
  }

  if (ownHostnames.some((own) => hostname === own.toLowerCase())) {
    return { ok: false, reason: 'link_shortener_loop' };
  }

  // Un host sin punto y que no es IP no resuelve fuera: suele ser un nombre
  // de la red interna del cliente.
  if (!hostname.includes('.') && !hostname.includes(':')) {
    return { ok: false, reason: 'private_host' };
  }

  return { ok: true, normalized: url.toString() };
}

/** Mensajes para la interfaz, en el idioma del producto. */
export const URL_REJECTION_MESSAGES: Readonly<Record<UrlRejectionReason, string>> = {
  empty: 'Escribe una dirección de destino.',
  too_long: `La dirección no puede superar los ${MAX_TARGET_URL_LENGTH} caracteres.`,
  malformed: 'La dirección no es válida. Debe empezar por https://',
  scheme_not_allowed: 'Solo se admiten direcciones http:// y https://',
  embedded_credentials: 'La dirección no puede incluir usuario y contraseña.',
  private_host: 'La dirección apunta a una red privada y no se puede usar.',
  loopback_host: 'La dirección apunta a este mismo equipo y no se puede usar.',
  link_shortener_loop: 'La dirección apunta al propio acortador y crearía un bucle.',
  blocked_host: 'Esta dirección está bloqueada por motivos de seguridad.',
};
